import { SavedViewsContextProvider } from "PFApp/components/filters/saved_views_context/saved_views_context";
import { checkFiltersAreClear } from "PFApp/use_filtered_collection/format_filters";
import Card from "PFComponents/card/card";
import { Typography } from "PFComponents/typography";
import { PageTarget } from "PFTypes/saved_filters";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NavigationPill } from "../components/navigation_pill";
import { MarketplacePage } from "../constants";
import { WorkOpportunitiesTab } from "../constants";
import { useMarketplaceContext } from "../contexts/marketplace_context";
import { useNavigationItems } from "../hooks/use_navigation_items";
import { useRolesStatistics } from "../hooks/use_roles_statistics";
import { EmptyState } from "./components/empty_state";
import { useEmptyState } from "./hooks/use_empty_state";
import { useRolesBrowser } from "./hooks/use_roles_browser";
import { useWorkOpportunitiesTab } from "./hooks/use_work_opportunities_tab";
import { Counters } from "./parts/counters";
import { FiltersArea } from "./parts/filters_area";
import { RoleDetails } from "./parts/role_details";
import { RolesBrowser } from "./parts/roles_browser";
import css from "./work_opportunities.module.scss";

const TabToTargetMap = {
  [WorkOpportunitiesTab.MatchingRoles]: PageTarget.MarketplaceHome,
  [WorkOpportunitiesTab.Interested]: PageTarget.MarketplaceInterests,
  [WorkOpportunitiesTab.SavedRoles]: PageTarget.MarketplaceSaved,
  [WorkOpportunitiesTab.SharedWithMe]: PageTarget.MarketplaceShared
};

const WorkOpportunitiesContent = ({ viewsKey }: { viewsKey: PageTarget }): JSX.Element => {
  const { t } = useTranslation("marketplace", { keyPrefix: "workOpportunities" });
  const {
    queryData: { isLoading, roles, meta, fetchNextPage, hasNextPage },
    setForceRefetchWithOrder
  } = useRolesBrowser();
  const statistics = useRolesStatistics(roles);
  const { selectedRoleId } = useMarketplaceContext();
  const items = useNavigationItems();

  const filtersAreClear = useMemo(() => checkFiltersAreClear(meta?.filters, []), [meta]);
  const isFiltered = !filtersAreClear;

  const { title, message, buttonLabel, handleButtonClick } = useEmptyState(isFiltered, viewsKey);

  const isTabEmpty = !isLoading && !roles.length;

  return (
    <div className={css.workOpportunitiesWrapper}>
      <div className={css.workOpportunities}>
        <div className={css.header}>
          <Typography variant="h1" noMargin>
            {t("title")}
          </Typography>
          <NavigationPill<MarketplacePage> activeItemId={MarketplacePage.WorkOpportunities} items={items} />
        </div>
        <FiltersArea meta={meta} viewsKey={viewsKey} filtersAreClear={filtersAreClear} />

        <Card shadowType="none" noPadding rootClassName={css.mainCard} bodyClassName={css.mainCardBody}>
          <Counters />
          {!isTabEmpty ? (
            <div className={css.columns}>
              <RolesBrowser
                isLoading={isLoading}
                roles={roles}
                meta={meta}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                setForceRefetchWithOrder={setForceRefetchWithOrder}
                statistics={statistics}
              />
              {selectedRoleId !== null && <RoleDetails />}
            </div>
          ) : (
            <div className={css.emptyStateWrapper}>
              <EmptyState
                isFiltered={isFiltered}
                title={title}
                message={message}
                buttonLabel={buttonLabel}
                handleButtonClick={handleButtonClick}
              />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export const WorkOpportunities = () => {
  const currentTab = useWorkOpportunitiesTab();
  const viewsKey = TabToTargetMap[currentTab!];

  return (
    <SavedViewsContextProvider viewsKey={viewsKey} withRelevantToMe={viewsKey === PageTarget.MarketplaceHome}>
      <WorkOpportunitiesContent viewsKey={viewsKey} />
    </SavedViewsContextProvider>
  );
};
